import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, Pencil, Trash2, Users, Star } from "lucide-react";
import api from "../lib/api";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import PageHeader from "../components/PageHeader";
import StatusBadge from "../components/StatusBadge";
import FormDialog from "../components/FormDialog";
import ConfirmDialog from "../components/ConfirmDialog";
import { FilterSelect } from "./Companies";
import AtecoInput from "../components/AtecoInput";
import CommittenteSelect, { CommittenteBadge } from "../components/CommittenteSelect";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { CONTACT_FIELDS, OPTIONS } from "../lib/entities";
import { useLookupOptions } from "../lib/lookups";
import { initials, fmtDate, label } from "../lib/format";

export default function Contacts() {
  const nav = useNavigate();
  const { can } = useAuth();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("all");
  const [country, setCountry] = useState("all");
  const [dm, setDm] = useState("all");
  const [ateco, setAteco] = useState("");
  const [sector, setSector] = useState("all");
  const [subsector, setSubsector] = useState("");
  const [committente, setCommittente] = useState("all");
  const countries = useLookupOptions("country"); const sectors = useLookupOptions("sector"); const subsectors = useLookupOptions("subsector");
  const [classification, setClassification] = useState("all");
  const [contactType, setContactType] = useState("all");
  const [edit, setEdit] = useState(null);
  const [create, setCreate] = useState(false);
  const [del, setDel] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    const params = {};
    if (search) params.search = search;
    if (status !== "all") params.relationship_status = status;
    if (country !== "all") params.country = country;
    if (dm !== "all") params.decision_maker = dm === "yes";
    if (ateco) params.ateco = ateco;
    if (sector !== "all") params.sector = sector;
    if (subsector) params.subsector = subsector;
    if (committente !== "all") params.committente = committente;
    if (classification !== "all") params.classification = classification;
    if (contactType !== "all") params.contact_type = contactType;
    const { data } = await api.get("/contacts", { params });
    setRows(data); setLoading(false);
  }, [search, status, country, dm, ateco, sector, subsector, committente, classification, contactType]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const h = () => load();
    window.addEventListener("crm-refresh", h);
    return () => window.removeEventListener("crm-refresh", h);
  }, [load]);

  const remove = async () => { await api.delete(`/contacts/${del.id}`); toast.success("Contatto eliminato"); setDel(null); load(); };

  return (
    <div>
      <PageHeader title="Contatti" subtitle={`${rows.length} persone`}>
        {can("write") && <Button data-testid="add-contact-button" onClick={() => setCreate(true)} className="bg-navy-900 hover:bg-navy-700 text-white gap-1.5"><Plus className="w-4 h-4" /> Nuovo Contatto</Button>}
      </PageHeader>

      <div className="flex flex-wrap gap-2.5 mb-4">
        <Input data-testid="contact-search" placeholder="Cerca…" value={search} onChange={(e) => setSearch(e.target.value)} className="w-full sm:w-64 h-10" />
        <CommittenteSelect mode="filter" testid="filter-contact-committente" value={committente} onChange={setCommittente} />
        <FilterSelect testid="filter-contact-type" value={contactType} onChange={setContactType} placeholder="Tipologia contatto" options={OPTIONS.contact_type} />
        <FilterSelect testid="filter-contact-classification" value={classification} onChange={setClassification} placeholder="Classif. azienda" options={OPTIONS.classification} />
        <FilterSelect testid="filter-contact-status" value={status} onChange={setStatus} placeholder="Stato" options={OPTIONS.contact_status} />
        <FilterSelect testid="filter-contact-country" value={country} onChange={setCountry} placeholder="Paese" options={countries} />
        <FilterSelect testid="filter-dm" value={dm} onChange={setDm} placeholder="Decision Maker" options={[{ value: "yes", label: "Sì" }, { value: "no", label: "No" }]} />
        <FilterSelect testid="filter-contact-sector" value={sector} onChange={setSector} placeholder="Settore" options={sectors} />
        <FilterSelect testid="filter-contact-subsector" value={subsector || "all"} onChange={(v) => setSubsector(v === "all" ? "" : v)} placeholder="Sottosettore" options={subsectors} />
        <AtecoInput testid="filter-contact-ateco" value={ateco} onChange={setAteco} placeholder="ATECO azienda" className="w-full sm:w-56" />
      </div>

      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden table-scroll table-wide">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-slate-100 text-left text-xs uppercase tracking-wider text-slate-400">
              <th className="px-4 py-3 font-medium">Nome</th>
              <th className="px-4 py-3 font-medium">Tipologia</th>
              <th className="px-4 py-3 font-medium">Ruolo</th>
              <th className="px-4 py-3 font-medium">Azienda</th>
              <th className="px-4 py-3 font-medium">Settore</th>
              <th className="px-4 py-3 font-medium">Stato relazione</th>
              <th className="px-4 py-3 font-medium">Ultimo contatto</th>
              <th className="px-4 py-3 font-medium text-right">Azioni</th>
            </tr>
          </thead>
          <tbody>
            {loading ? <tr><td colSpan={8} className="px-4 py-10 text-center text-slate-400">Caricamento…</td></tr>
              : rows.length === 0 ? <tr><td colSpan={8} className="px-4 py-14 text-center text-slate-400"><Users className="w-8 h-8 mx-auto mb-2 opacity-40" />Nessun contatto</td></tr>
              : rows.map((c) => (
                <tr key={c.id} data-testid={`contact-row-${c.id}`} onClick={() => nav(`/contatti/${c.id}`)} className="border-b border-slate-50 hover:bg-slate-50 cursor-pointer">
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2.5">
                      <div className="w-8 h-8 rounded-full bg-teal-50 flex items-center justify-center text-teal-700 font-semibold text-xs">{initials(`${c.first_name} ${c.last_name}`)}</div>
                      <div>
                        <p className="font-medium text-slate-800 flex items-center gap-1.5">{c.first_name} {c.last_name} {c.decision_maker && <Star className="w-3.5 h-3.5 text-amber-500 fill-amber-500" title="Decision Maker" />}<CommittenteBadge value={c.committente} testid={`contact-committente-${c.id}`} /></p>
                        <p className="text-xs text-slate-400">{c.email}</p>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3" data-testid={`contact-type-${c.id}`}>
                    <StatusBadge group="contact_type" value={c.contact_type} />
                    {c.company?.classification && <p className="text-[10px] text-slate-400 mt-1 uppercase tracking-wide whitespace-nowrap">Az.: {label("classification", c.company.classification)}</p>}
                  </td>
                  <td className="px-4 py-3 text-slate-600">{c.job_title || "—"}</td>
                  <td className="px-4 py-3 text-slate-600">
                    {c.company?.legal_name || "—"}
                    {c.company?.ateco && <p data-testid={`contact-ateco-${c.id}`} className="text-xs text-slate-400 mt-0.5 truncate max-w-[14rem]" title={c.company.ateco_label || ""}><span className="font-mono text-teal-700">{c.company.ateco}</span>{c.company.ateco_label && ` · ${c.company.ateco_label}`}</p>}
                  </td>
                  <td className="px-4 py-3 text-slate-600" data-testid={`contact-sector-${c.id}`}>
                    {c.company?.sector || "—"}
                    {c.company?.subsector && <p className="text-xs text-slate-500 mt-0.5">{c.company.subsector}</p>}
                  </td>
                  <td className="px-4 py-3"><StatusBadge group="contact_status" value={c.relationship_status} /></td>
                  <td className="px-4 py-3 text-slate-500 font-mono text-xs">{fmtDate(c.last_contact)}</td>
                  <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                    <div className="flex items-center justify-end gap-1">
                      {can("write") && <button data-testid={`edit-contact-${c.id}`} onClick={() => setEdit(c)} className="icon-btn p-1.5 rounded-lg hover:bg-slate-100 text-slate-500"><Pencil className="w-4 h-4" /></button>}
                      {can("delete") && <button data-testid={`delete-contact-${c.id}`} onClick={() => setDel(c)} className="icon-btn p-1.5 rounded-lg hover:bg-red-50 text-red-500"><Trash2 className="w-4 h-4" /></button>}
                    </div>
                  </td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>

      <FormDialog open={create} onOpenChange={setCreate} title="Nuovo Contatto" fields={CONTACT_FIELDS} endpoint="/contacts" onSaved={load} />
      {edit && <FormDialog open={!!edit} onOpenChange={(o) => !o && setEdit(null)} title="Modifica Contatto" fields={CONTACT_FIELDS} initial={edit} endpoint={`/contacts/${edit.id}`} method="put" onSaved={load} />}
      <ConfirmDialog open={!!del} onOpenChange={(o) => !o && setDel(null)} onConfirm={remove} description={`Eliminare "${del?.first_name} ${del?.last_name}"?`} />
    </div>
  );
}
